import React, {useRef, useState} from 'react';
import {analysisApi} from '../services/api';
import {AnalysisResult, BulkAnalysisResult} from '../types';
import CountryMultiSelect from './CountryMultiSelect';
import AnalysisResultsTable from './AnalysisResultsTable';
import {exportAllResults} from '../utils/exportUtils';
import {MAX_DOMAIN_FILTER, parseDomainFilter} from '../utils/domainFilter';
import {RelevanceEngineConfig} from './relevance-engine/useRelevanceEngineConfig';
import {Button} from './ui/button';
import {StatCard} from './ui/StatCard';

interface ContextAnalyzerProps {
    config: RelevanceEngineConfig;
}

const ContextAnalyzer: React.FC<ContextAnalyzerProps> = ({config}) => {
    const {selectedPromptId, selectedProvider, selectedModel, maxUrlsNonAdmin, isAdmin} = config;

    // Common states
    const [urlsText, setUrlsText] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [result, setResult] = useState<BulkAnalysisResult | null>(null);
    const [partialResults, setPartialResults] = useState<AnalysisResult[]>([]);
    const partialResultsRef = useRef<AnalysisResult[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [excludedUrlsCount, setExcludedUrlsCount] = useState<number | null>(null);
    const [originalRequestedCount, setOriginalRequestedCount] = useState<number | null>(null);
    const [progress, setProgress] = useState({
        current: 0,
        total: 0,
        currentChunk: 0,
        totalChunks: 0,
        status: '',
        currentAction: 0,
        totalActions: 0,
        actionType: '' as 'fetching' | 'analyzing' | ''
    });
    const [streamingAbort, setStreamingAbort] = useState<(() => void) | null>(null);

    // Export menu state
    const [showExportMenu, setShowExportMenu] = useState(false);

    // Input mode state
    const [inputMode, setInputMode] = useState<'manual' | 'database'>('manual');
    const [databaseUrlCount, setDatabaseUrlCount] = useState(10);
    const [isLoadingDatabaseUrls, setIsLoadingDatabaseUrls] = useState(false);
    const [databaseUrls, setDatabaseUrls] = useState<{ urls: string[], records: any[] }>({urls: [], records: []});
    // Default (DB) mode is multi-country (bd-deepcontext-m79a4.2): the selector
    // produces an array of ISO country codes.
    const [selectedCountryCodes, setSelectedCountryCodes] = useState<string[]>([]);
    const [domainFilter, setDomainFilter] = useState<string>('');
    // Country-distribution mode for multi-country DB fetches (bd-g6m3q.3).
    // Defaults to the backend default ('uniform', bd-yy3mv).
    const [countryDistributionMode, setCountryDistributionMode] = useState<'uniform' | 'proportional'>('uniform');
    // Exclude homepages from DB URL fetch (bd-deepcontext-vql07). Default
    // CHECKED (true), matching the scheduled-jobs control.
    const [excludeHomepages, setExcludeHomepages] = useState(true);

    // File upload state
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

    const handleAnalyze = async () => {
        const isFromDatabase = inputMode === 'database';
        const urls = urlsText.split('\n').filter(line => line.trim());

        if (!isFromDatabase && urls.length === 0) {
            setError('Please enter at least one valid URL');
            return;
        }
        const maxUrls = isAdmin ? 10000 : maxUrlsNonAdmin;

        if (!isFromDatabase && urls.length > maxUrls) {
            setError(`Maximum ${maxUrls} URLs allowed per batch`);
            return;
        }

        setIsAnalyzing(true);
        setError(null);
        setWarning(null);
        setExcludedUrlsCount(null);
        setResult(null);
        setPartialResults([]);
        partialResultsRef.current = [];
        setShowExportMenu(false);

        const estimatedTotal = isFromDatabase ? databaseUrlCount : urls.length;
        const totalChunks = Math.ceil(estimatedTotal / 10);
        // Total actions = fetching URLs + analyzing URLs
        const totalActions = estimatedTotal * 2;

        // Store original requested count for display
        setOriginalRequestedCount(estimatedTotal);

        setProgress({
            current: 0,
            total: estimatedTotal,
            currentChunk: 0,
            totalChunks,
            status: 'Starting analysis...',
            currentAction: 0,
            totalActions: totalActions,
            actionType: 'fetching' as 'fetching'
        });

        try {
            const analyzeRequest: any = {
                urls: isFromDatabase && databaseUrlCount > 100 ? [] : isFromDatabase ? databaseUrls.records.slice(0, 10) : urls,
                promptId: selectedPromptId,
                provider: selectedProvider,
                model: selectedModel
            };

            if (isFromDatabase) {
                analyzeRequest.analyzeFromDatabase = true;
                analyzeRequest.databaseUrlCount = databaseUrlCount;
                if (selectedCountryCodes.length > 0) {
                    analyzeRequest.countryCodes = selectedCountryCodes;
                }
                // Country-distribution mode is only meaningful with >1 country
                // selected; always send it so the backend honors the choice.
                analyzeRequest.countryDistributionMode = countryDistributionMode;
                // Homepage exclusion (bd-deepcontext-vql07) — mirrors scheduled jobs.
                analyzeRequest.excludeHomepages = excludeHomepages;
                // Pass sanitized domains for server-side filtering
                const domains = parseDomainFilter(domainFilter);
                if (domains.length > 0) {
                    analyzeRequest.domains = domains;
                }
            }


            const {abort} = await analysisApi.analyzeStreaming(
                analyzeRequest,
                {
                    onProgress: (data: any) => {
                        // Progress events from the streaming API
                        setProgress(prev => {
                            // Handle new chunk-based progress data
                            if (data.chunksCompleted !== undefined && data.totalChunks !== undefined) {
                                // Queue-based processing with chunk progress
                                // Use actual total from backend if provided, otherwise calculate from chunks
                                // Queue uses 20 URLs per chunk, direct analysis uses 10-25 per chunk
                                const chunkSize = data.total ? Math.ceil((data.total || 0) / data.totalChunks) : 20;
                                // Prioritize: data.total > calculated from chunks > prev.total
                                // This ensures we use the correct total even if prev.total was the initial estimate
                                const calculatedTotal = data.totalChunks * chunkSize;
                                const totalUrls = data.total !== undefined ? data.total : (calculatedTotal || prev.total);
                                const urlsProcessed = data.current !== undefined ? data.current : (data.chunksCompleted * chunkSize);

                                return {
                                    ...prev,
                                    current: urlsProcessed,
                                    total: totalUrls, // Use calculated total from chunks if data.total not provided
                                    currentChunk: data.chunksCompleted,
                                    totalChunks: data.totalChunks,
                                    totalActions: totalUrls * 2, // Total actions = fetch + analyze
                                    currentAction: totalUrls + urlsProcessed, // URLs fetched + URLs analyzed
                                    status: data.message || `Processing chunks: ${data.chunksCompleted}/${data.totalChunks} (${data.progress}%)`,
                                    actionType: 'analyzing' as 'analyzing'
                                };
                            }

                            // Handle 'queued' status - update total immediately when backend provides it
                            if (data.status === 'queued' && data.total !== undefined) {
                                return {
                                    ...prev,
                                    total: data.total, // Use backend's actual total (after exclusions)
                                    totalChunks: data.totalChunks !== undefined ? data.totalChunks : prev.totalChunks,
                                    totalActions: data.total * 2, // Total actions = fetch + analyze
                                    status: data.message || data.status || prev.status || 'Queued...',
                                    currentAction: 0,
                                    actionType: 'fetching' as 'fetching'
                                };
                            }

                            // Legacy progress handling
                            let actionsCompleted = prev.currentAction;
                            let actionType = prev.actionType;

                            // If we see "Loaded X URLs from database" or similar, URLs are fetched
                            if (data.status && data.status.includes('Loaded') && data.status.includes('URLs')) {
                                // All URLs have been fetched
                                actionsCompleted = data.total; // Fetching complete
                                actionType = 'analyzing' as 'analyzing';
                            } else if (data.status && data.status.includes('Starting analysis')) {
                                // Moving from fetching to analyzing
                                actionsCompleted = data.total; // Fetching complete
                                actionType = 'analyzing' as 'analyzing';
                            }

                            return {
                                ...prev,
                                total: data.total !== undefined ? data.total : prev.total, // Preserve previous total if not provided
                                current: data.current !== undefined ? data.current : prev.current, // Preserve current if not provided
                                totalChunks: data.totalChunks !== undefined ? data.totalChunks : prev.totalChunks,
                                currentChunk: data.currentChunk !== undefined ? data.currentChunk : prev.currentChunk,
                                totalActions: (data.total !== undefined ? data.total : prev.total) * 2, // Total actions = fetch + analyze for each URL
                                status: data.status || data.message || prev.status || 'Processing...',
                                currentAction: actionsCompleted,
                                actionType: actionType
                            };
                        });
                        console.log('Progress update:', data);
                    },
                    onChunkStart: (data) => {
                        console.log('Chunk started:', data);
                        // Update status when chunk starts analyzing
                        setProgress(prev => ({
                            ...prev,
                            currentChunk: data.chunkIndex,
                            totalChunks: data.totalChunks,
                            status: `Analyzing chunk ${data.chunkIndex}/${data.totalChunks} (${data.urlsInChunk} URLs)...`,
                            actionType: 'analyzing' as 'analyzing'
                        }));
                    },
                    onWarning: (data) => {
                        console.log('Warning:', data);
                        // Show warning message to user about excluded URLs
                        if (data.message) {
                            setWarning(data.message);
                            // Calculate excluded URLs count
                            if (data.urlsRequested !== undefined && data.urlsFound !== undefined) {
                                const excluded = data.urlsRequested - data.urlsFound;
                                setExcludedUrlsCount(excluded > 0 ? excluded : null);
                            }
                            // Update progress status to show the warning
                            setProgress(prev => ({
                                ...prev,
                                status: data.message || prev.status,
                                // Update total if provided (actual URLs found after exclusion)
                                total: data.urlsFound !== undefined ? data.urlsFound : prev.total
                            }));
                        }
                    },
                    onChunkComplete: (data) => {
                        console.log('Chunk complete:', data);
                        const newResults = data.results || [];
                        console.log('Received results with IDs:', newResults.map((r: any) => r.id));
                        partialResultsRef.current = [...partialResultsRef.current, ...newResults];
                        setPartialResults([...partialResultsRef.current]);

                        // Calculate progress based on actual results received
                        const totalUrlsCompleted = partialResultsRef.current.length;
                        setProgress(prev => {
                            // Actions completed = all URLs fetched + URLs analyzed so far
                            const actionsCompleted = prev.total + totalUrlsCompleted;

                            return {
                                ...prev,
                                current: totalUrlsCompleted,
                                currentAction: actionsCompleted,
                                currentChunk: data.chunkIndex || prev.currentChunk,
                                status: totalUrlsCompleted < prev.total ?
                                    `Analyzed ${totalUrlsCompleted}/${prev.total} URLs` :
                                    'Finalizing results...'
                            };
                        });
                    },
                    onComplete: (data) => {
                        console.log('Analysis complete:', data);

                        const finalResults = partialResultsRef.current;
                        // Use backend's summary if available, otherwise calculate from results
                        const backendSummary = data.summary || {};
                        const finalResult: BulkAnalysisResult = {
                            id: data.bulkResultId || '',
                            promptId: selectedPromptId,
                            results: finalResults,
                            totalCost: data.totalCost || 0,
                            analyzedAt: data.analyzedAt || new Date().toISOString(),
                            summary: {
                                // Use backend's totalUrls (accounts for exclusions), fallback to results length
                                totalUrls: backendSummary.totalUrls !== undefined ? backendSummary.totalUrls : finalResults.length,
                                matchedUrls: backendSummary.matchedUrls !== undefined ? backendSummary.matchedUrls : finalResults.filter((r: AnalysisResult) => r.isBrandSafe).length,
                                unmatchedUrls: backendSummary.unmatchedUrls !== undefined ? backendSummary.unmatchedUrls : finalResults.filter((r: AnalysisResult) => !r.isBrandSafe).length,
                                failedUrls: backendSummary.failedUrls !== undefined ? backendSummary.failedUrls : (backendSummary.totalUrls !== undefined ? backendSummary.totalUrls - (backendSummary.matchedUrls || 0) - (backendSummary.unmatchedUrls || 0) : 0),
                                averageConfidence: backendSummary.averageConfidence !== undefined ? backendSummary.averageConfidence : (finalResults.reduce((sum: number, r: AnalysisResult) => sum + r.confidence, 0) / finalResults.length || 0),
                                totalInputTokens: backendSummary.totalInputTokens !== undefined ? backendSummary.totalInputTokens : finalResults.reduce((sum: number, r: AnalysisResult) => sum + (r.inputTokens || 0), 0),
                                totalOutputTokens: backendSummary.totalOutputTokens !== undefined ? backendSummary.totalOutputTokens : finalResults.reduce((sum: number, r: AnalysisResult) => sum + (r.outputTokens || 0), 0)
                            }
                        };

                        setResult(finalResult);
                        setPartialResults([]);
                        partialResultsRef.current = [];
                        setIsAnalyzing(false);
                        setStreamingAbort(null);
                        // Keep warning visible after completion to show why URLs were excluded
                        setProgress({
                            current: 0,
                            total: 0,
                            currentChunk: 0,
                            totalChunks: 0,
                            status: '',
                            currentAction: 0,
                            totalActions: 0,
                            actionType: '' as ''
                        });
                    },
                    onError: (error) => {
                        console.error('Stream error:', error);
                        setError(error);
                        setIsAnalyzing(false);
                        setStreamingAbort(null);
                        setProgress({
                            current: 0,
                            total: 0,
                            currentChunk: 0,
                            totalChunks: 0,
                            status: '',
                            currentAction: 0,
                            totalActions: 0,
                            actionType: '' as ''
                        });
                    }
                }
            );

            setStreamingAbort(() => abort);

        } catch (err: any) {
            if (err.name === 'AbortError') {
                setError('Analysis was cancelled');
            } else {
                console.error('Analysis error:', err);
                setError(err.message || 'Failed to analyze URLs');
            }
            setIsAnalyzing(false);
            setStreamingAbort(null);
            setProgress({
                current: 0,
                total: 0,
                currentChunk: 0,
                totalChunks: 0,
                status: '',
                currentAction: 0,
                totalActions: 0,
                actionType: '' as ''
            });
        }
    };

    const handleCancelAnalysis = () => {
        if (streamingAbort) {
            streamingAbort();
            setIsAnalyzing(false);
            setError('Analysis cancelled by user');
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            const urls = content.split(/[\n,]/).map(url => url.trim()).filter(url => url);
            setUrlsText(urls.join('\n'));
            setUploadedFileName(file.name);
        };
        reader.readAsText(file);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);

        const file = e.dataTransfer.files[0];
        if (file && (file.type === 'text/plain' || file.type === 'text/csv' || file.name.endsWith('.txt') || file.name.endsWith('.csv'))) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const content = event.target?.result as string;
                const urls = content.split(/[\n,]/).map(url => url.trim()).filter(url => url);
                setUrlsText(urls.join('\n'));
                setUploadedFileName(file.name);
            };
            reader.readAsText(file);
        }
    };


    const loadDatabaseUrls = async () => {
        // Parse and sanitize domain filter
        const domains = parseDomainFilter(domainFilter);

        // Enforce domain limit
        if (domains.length > MAX_DOMAIN_FILTER) {
            setError(`Maximum ${MAX_DOMAIN_FILTER} domains allowed. You have ${domains.length} domains.`);
            return;
        }

        setIsLoadingDatabaseUrls(true);
        setError(null);

        try {
            // With a blueprint selected, send the same filters Analyze applies
            // (blueprint, every country, distribution mode) so the preview lists
            // what will be scanned. Without one, preview the first market only.
            const previewCountry = selectedCountryCodes.length > 0 ? selectedCountryCodes[0].toLowerCase() : undefined;
            const response = await analysisApi.getDatabaseUrls(
                databaseUrlCount, previewCountry, domains.length > 0 ? domains : undefined, excludeHomepages,
                selectedPromptId
                    ? {promptId: selectedPromptId, countryCodes: selectedCountryCodes, countryDistributionMode}
                    : undefined,
            );

            setDatabaseUrls({
                urls: response.urls,
                records: response.records
            });

            const urls = response.urls.join('\n');
            setUrlsText(urls);
        } catch (err: any) {
            setError(err.message || 'Failed to preview URLs from database');
        } finally {
            setIsLoadingDatabaseUrls(false);
        }
    };

    const displayResults = partialResults.length > 0 ? partialResults : (result?.results || []);

    // Get labels
    const getLabels = () => {
        return {
            matched: 'Match',
            unmatched: 'No Match',
            matchLabel: 'Match',
            unmatchLabel: 'No Match'
        };
    };

    const labels = getLabels();

    return (
        <div>
            <div>
                <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-ink">Input Mode:</span>
                        <div className="inline-flex rounded-full bg-ghost-bg p-[3px]">
                            <Button
                                type="button"
                                variant="pill"
                                active={inputMode === 'manual'}
                                onClick={() => {
                                    setInputMode('manual');
                                    setUrlsText('');
                                    setUploadedFileName(null);
                                    setDatabaseUrls({urls: [], records: []});
                                }}
                            >
                                Manual Input
                            </Button>
                            <Button
                                type="button"
                                variant="pill"
                                active={inputMode === 'database'}
                                onClick={() => {
                                    setInputMode('database');
                                    setUrlsText('');
                                    setUploadedFileName(null);
                                    setDatabaseUrls({urls: [], records: []});
                                }}
                            >
                                From Database
                            </Button>
                        </div>
                    </div>
                    <p className="text-sm text-ink-muted mt-2">
                        {inputMode === 'manual'
                            ? 'Enter URLs manually or upload a file'
                            : 'Load top URLs from RTB stats database sorted by traffic count'
                        }
                    </p>
                </div>

                <div className="space-y-4">
                    {/* Database URL Selection */}
                    {inputMode === 'database' && (
                        <div>
                            <div className="border-t border-line pt-4"/>
                            <h3 className="text-lg font-medium text-ink mb-3 mt-4">Database URL Selection</h3>

                            <div className="space-y-4">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
                                    <div>
                                        <label htmlFor="urlCount"
                                               className="block text-sm font-medium text-ink-muted mb-2">
                                            Number of URLs to fetch
                                            (1-{isAdmin ? '10,000' : maxUrlsNonAdmin.toLocaleString()})
                                        </label>
                                        <input
                                            id="urlCount"
                                            type="number"
                                            min="1"
                                            max={isAdmin ? "10000" : maxUrlsNonAdmin.toString()}
                                            value={databaseUrlCount}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value) || 1;
                                                setDatabaseUrlCount(Math.min(Math.max(value, 1), isAdmin ? 10000 : maxUrlsNonAdmin));
                                            }}
                                            className="w-full h-10 px-3 py-2 border rounded-lg focus:ring-accent focus:border-accent bg-surface border-line text-ink"
                                            placeholder="10"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="countrySelect"
                                               className="block text-sm font-medium text-ink-muted mb-2">
                                            Country Filter (optional)
                                        </label>
                                        <CountryMultiSelect
                                            value={selectedCountryCodes}
                                            onChange={setSelectedCountryCodes}
                                            placeholder="All countries"
                                        />
                                    </div>
                                    <div>
                                        <button
                                            onClick={loadDatabaseUrls}
                                            disabled={isLoadingDatabaseUrls}
                                            className={`w-full h-10 px-4 py-2 font-medium rounded-lg transition-colors ${
                                                isLoadingDatabaseUrls
                                                    ? 'text-white bg-ink-muted cursor-not-allowed'
                                                    : 'text-sidebar bg-accent hover:bg-accent-active'
                                            }`}
                                        >
                                            {isLoadingDatabaseUrls ? 'Loading URLs...' : 'Preview URLs'}
                                        </button>
                                    </div>
                                </div>

                                {/* Domain Filter */}
                                <div className="mt-4">
                                    <label htmlFor="domainFilter"
                                           className="block text-sm font-medium text-ink-muted mb-2">
                                        Domain Filter (optional, max {MAX_DOMAIN_FILTER})
                                        <span className="ml-1 relative inline-block group">
                      <svg className="w-4 h-4 text-ink-muted hover:text-ink cursor-help inline" fill="none"
                           stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span
                          className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-ink rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        Only preview URLs from these domains (max {MAX_DOMAIN_FILTER})
                      </span>
                    </span>
                                    </label>
                                    <textarea
                                        id="domainFilter"
                                        value={domainFilter}
                                        onChange={(e) => setDomainFilter(e.target.value)}
                                        placeholder="Enter domains (one per line or comma-separated)&#10;e.g. example.com, news.com"
                                        rows={3}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-accent focus:border-accent text-sm bg-surface text-ink ${
                                            parseDomainFilter(domainFilter).length > MAX_DOMAIN_FILTER ? 'border-risk' : 'border-line'
                                        }`}
                                    />
                                    {domainFilter.trim() && (
                                        <p className={`mt-1 text-xs ${parseDomainFilter(domainFilter).length > MAX_DOMAIN_FILTER ? 'text-risk' : 'text-ink-muted'}`}>
                                            {parseDomainFilter(domainFilter).length}/{MAX_DOMAIN_FILTER} domain(s)
                                            specified
                                            {parseDomainFilter(domainFilter).length > MAX_DOMAIN_FILTER && ' - exceeds limit!'}
                                        </p>
                                    )}
                                </div>

                                {/* Country Distribution (bd-g6m3q.3) — only
                                    meaningful with multiple countries selected. */}
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-ink-muted mb-2">
                                        Country Distribution
                                    </label>
                                    <div className="inline-flex w-max rounded-full bg-ghost-bg p-[3px]">
                                        <Button
                                            type="button"
                                            variant="pill"
                                            size="sm"
                                            active={countryDistributionMode === 'proportional'}
                                            onClick={() => setCountryDistributionMode('proportional')}
                                        >
                                            Proportional
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="pill"
                                            size="sm"
                                            active={countryDistributionMode === 'uniform'}
                                            onClick={() => setCountryDistributionMode('uniform')}
                                        >
                                            Uniform
                                        </Button>
                                    </div>
                                    <p className="text-xs text-ink-muted mt-1">
                                        {countryDistributionMode === 'proportional'
                                            ? 'Distributes URLs based on available pool size per country'
                                            : 'Distributes URLs evenly across countries'}
                                    </p>
                                </div>

                                {/* Exclude Homepages (bd-deepcontext-vql07) —
                                    mirrors the scheduled-jobs control, default checked. */}
                                <div className="mt-4">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={excludeHomepages}
                                            onChange={(e) => setExcludeHomepages(e.target.checked)}
                                            className="h-4 w-4 rounded border-line accent-accent focus:ring-accent"
                                        />
                                        <span className="text-sm font-medium text-ink-muted">Exclude Homepages</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Manual URL Input */}
                    {inputMode === 'manual' && (
                        <>
                            <div
                                className={`relative ${isDragOver ? 'ring-2 ring-accent ring-opacity-50' : ''}`}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setIsDragOver(true);
                                }}
                                onDragLeave={() => setIsDragOver(false)}
                                onDrop={handleDrop}
                            >
                                <label htmlFor="urls" className="block text-sm font-medium text-ink mb-2">
                                    Enter URLs (one per line) or drag & drop a file
                                </label>
                                <textarea
                                    id="urls"
                                    rows={7}
                                    className="block w-full px-3 py-2 border border-line bg-surface text-ink rounded-xl focus:ring-accent focus:border-accent"
                                    placeholder="https://example.com&#10;https://another-example.com&#10;..."
                                    value={urlsText}
                                    onChange={(e) => {
                                        const decodedValue = e.target.value.split('\n').map(url => {
                                            const trimmedUrl = url.trim();
                                            if (!trimmedUrl) return '';
                                            try {
                                                return decodeURIComponent(trimmedUrl);
                                            } catch {
                                                return trimmedUrl;
                                            }
                                        }).join('\n');
                                        setUrlsText(decodedValue);
                                    }}
                                />
                                {isDragOver && (
                                    <div
                                        className="absolute inset-0 bg-nav-active bg-opacity-90 flex items-center justify-center rounded-md">
                                        <p className="text-accent-active font-medium">Drop your file here</p>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-4">
                                <label
                                    className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-1.5 text-sm font-semibold text-ink hover:bg-ghost-bg cursor-pointer">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                                    </svg>
                                    Upload File
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept=".txt,.csv"
                                        onChange={handleFileUpload}
                                    />
                                </label>
                                {uploadedFileName && (
                                    <span className="text-sm text-ink-muted">
                    Uploaded: {uploadedFileName}
                  </span>
                                )}
                                <span className="text-sm text-ink-muted">
                  Supports .txt and .csv files
                </span>
                            </div>
                        </>
                    )}

                    {/* Database mode URL display */}
                    {inputMode === 'database' && urlsText && (
                        <div>
                            <label htmlFor="urls" className="block text-sm font-medium text-ink-muted mb-2">
                                URLs to Analyze
                            </label>
                            <textarea
                                id="urls"
                                value={urlsText ? urlsText.split('\n').map(url => {
                                    try {
                                        return decodeURIComponent(url.trim());
                                    } catch {
                                        return url.trim();
                                    }
                                }).join('\n') : ''}
                                onChange={(e) => setUrlsText(e.target.value)}
                                readOnly
                                rows={10}
                                className="w-full px-3 py-2 border border-line rounded-md bg-ghost-bg text-ink text-sm"
                                placeholder="No URLs selected. Use the 'Preview URLs' section above to fetch URLs from the database."
                            />
                        </div>
                    )}

                    <div className="flex gap-2">
                        <button
                            onClick={handleAnalyze}
                            disabled={isAnalyzing || (inputMode === 'manual' && !urlsText.trim()) || !selectedPromptId}
                            className={`rounded-full bg-gradient-accent px-5 py-2 text-sm font-bold text-white focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                                isAnalyzing ? 'opacity-50' : 'hover:opacity-90'
                            }`}
                        >
                            {isAnalyzing ? 'Analyzing URLs...' : 'Analyze URLs'}
                        </button>
                        {isAnalyzing && streamingAbort && (
                            <button
                                type="button"
                                onClick={handleCancelAnalysis}
                                className="rounded-full bg-risk-bg px-4 py-2 text-sm font-bold text-risk hover:opacity-80 focus:ring-2 focus:ring-risk focus:ring-offset-2"
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Progress Display */}
            {isAnalyzing && (
                <div className="bg-surface rounded-lg shadow-sm border border-line p-6 mt-6">
                    <h3 className="text-lg font-semibold text-ink mb-4 flex items-center gap-3">
                        Analysis Progress
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-accent"></div>
                    </h3>
                    <div className="space-y-4">
                        <div>
                            <div className="flex justify-between text-sm text-ink-muted mb-1">
                <span className="flex items-center gap-2">
                  {progress.status}
                </span>
                                <span className="flex items-center gap-2">
                  {progress.current}/{progress.total} URLs
                                    {originalRequestedCount && originalRequestedCount !== progress.total && (
                                        <span className="text-xs text-ink-muted">
                      (of {originalRequestedCount} requested)
                    </span>
                                    )}
                </span>
                            </div>
                            <div className="w-full bg-line rounded-full h-3 overflow-hidden">
                                <div
                                    className="bg-accent h-3 rounded-full transition-all duration-500 relative"
                                    style={{width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`}}
                                >
                                    <div className="absolute inset-0 bg-white bg-opacity-30 animate-pulse"></div>
                                </div>
                            </div>
                            <div className="flex justify-between text-xs text-ink-muted mt-1">
                <span>
                  {progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}% complete
                </span>
                                {progress.totalChunks > 1 && (
                                    <span>Chunk {progress.currentChunk}/{progress.totalChunks}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Warning Display */}
            {warning && (
                <div className="bg-warn-bg border-l-4 border-warn p-4 mt-6">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-warn" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd"
                                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                      clipRule="evenodd"/>
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-warn">{warning}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Display */}
            {error && (
                <div className="bg-risk-bg border-l-4 border-risk p-4 mt-6">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-risk" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                      clipRule="evenodd"/>
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-risk">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Results Section */}
            {(result || partialResults.length > 0) && (
                <div className="mt-8 space-y-6">
                    {/* Summary Statistics */}
                    {result && (
                        <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
                            <h3 className="text-lg font-semibold text-ink mb-4">Analysis Summary</h3>
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                <StatCard
                                    testId="summary-stat-total-urls"
                                    label="Total URLs"
                                    value={result.summary.totalUrls}
                                    caption={excludedUrlsCount && excludedUrlsCount > 0
                                        ? <span className="text-warn">({excludedUrlsCount} excluded)</span>
                                        : undefined}
                                />
                                <StatCard
                                    testId="summary-stat-matched"
                                    label={labels.matched}
                                    value={result.summary.matchedUrls || 0}
                                    valueColorClassName="text-safe"
                                />
                                <StatCard
                                    testId="summary-stat-unmatched"
                                    label={labels.unmatched}
                                    value={result.summary.unmatchedUrls || 0}
                                    valueColorClassName="text-risk"
                                />
                                {(result.summary.failedUrls ?? 0) > 0 && (
                                    <StatCard
                                        testId="summary-stat-failed"
                                        label="Failed"
                                        value={result.summary.failedUrls}
                                        valueColorClassName="text-warn"
                                    />
                                )}
                                <StatCard
                                    testId="summary-stat-avg-confidence"
                                    label="Avg Confidence"
                                    value={`${Math.round((result.summary.averageConfidence || 0) * 100)}%`}
                                />
                            </div>

                            {/* Token Usage for Admin */}
                            {result.summary.totalInputTokens && result.summary.totalOutputTokens && isAdmin && (
                                <div className="mt-4 pt-4 border-t border-line">
                                    <div className="grid grid-cols-3 gap-4 text-sm">
                                        <div>
                                            <span className="text-ink-muted">Input Tokens:</span>
                                            <span
                                                className="ml-2 font-medium text-ink">{result.summary.totalInputTokens.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span className="text-ink-muted">Output Tokens:</span>
                                            <span
                                                className="ml-2 font-medium text-ink">{result.summary.totalOutputTokens.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span className="text-ink-muted">Total Cost:</span>
                                            <span className="ml-2 font-medium text-ink">${result.totalCost.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Export Section */}
                    {displayResults.length > 0 && (
                        <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold text-ink">Analysis Results</h3>
                                <div className="flex items-center gap-4">
                                    {/* Export results */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowExportMenu(!showExportMenu)}
                                            className="px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-active focus:ring-2 focus:ring-accent focus:ring-offset-2 flex items-center gap-2"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor"
                                                 viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"/>
                                            </svg>
                                            Export Results
                                        </button>
                                        {showExportMenu && (
                                            <div
                                                className="absolute right-0 mt-2 w-48 bg-surface border border-line rounded-md shadow-lg z-10">
                                                <button
                                                    onClick={() => {
                                                        exportAllResults(displayResults, 'csv', result || undefined, false);
                                                        setShowExportMenu(false);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-ghost-bg"
                                                >
                                                    Export as CSV
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        exportAllResults(displayResults, 'excel', result || undefined, false);
                                                        setShowExportMenu(false);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-ghost-bg"
                                                >
                                                    Export as Excel
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Results table — one shared paginated table for
                                both Relevance Engine modes (deepcontext-8snj). */}
                            <div className="mt-6">
                                <AnalysisResultsTable
                                    results={displayResults}
                                    onFeedbackSubmitted={(id, agree) => {
                                        // Update the result in displayResults
                                        setPartialResults(prev => prev.map(r =>
                                            r.id === id
                                                ? {...r, feedback: {agree, timestamp: new Date().toISOString()}}
                                                : r
                                        ));

                                        // Update result if it exists
                                        if (result) {
                                            const updatedResults = result.results.map(r =>
                                                r.id === id
                                                    ? {...r, feedback: {agree, timestamp: new Date().toISOString()}}
                                                    : r
                                            );
                                            setResult({...result, results: updatedResults});
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ContextAnalyzer;
